import React, { useCallback, useRef, useState } from 'react';
import { GridLayout, MIN_COLUMN_WIDTH_PX, frozenLeftOffsets } from './gridLayout';

/**
 * Applies the column layout to the quotation grid (ClickUp 86d478wd1).
 *
 * The grid's 23 header cells and each row's 23 cells are hand-written JSX in a
 * fixed source order. Rather than restructure that into a driven array — a
 * rewrite of the largest render block in the codebase — these components take
 * the cells as CHILDREN and reorder them. The cells stay in their canonical
 * order in the source, which is what makes them readable; the display order
 * lives in `gridLayout.ts` alone.
 *
 * Both components render a Fragment, so the cells remain DIRECT children of the
 * CSS grid. Wrapping them in a div would break the grid outright.
 */

/** Children as a stable array, with the count checked against the layout. */
function orderedChildren(children: React.ReactNode, order: number[], label: string) {
  const cells = React.Children.toArray(children);

  if (cells.length !== order.length) {
    // The one failure that must never render: a cell count that does not match
    // the layout means a column was added or removed without updating
    // QUOTATION_GRID_DEFAULT_WIDTHS, and reordering would drop or duplicate a
    // cell — silently, and differently in the header than in the rows, so the
    // grid would show one column's values under another column's heading.
    // Falling back to source order keeps the grid CORRECT and merely unsorted.
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error(
        `[QuotationGrid] ${label}: ${cells.length} cells but the layout has ${order.length} columns. ` +
        'Add or remove the column in QUOTATION_GRID_DEFAULT_WIDTHS. Rendering in source order.'
      );
    }
    return null;
  }

  return order.map((canonical) => cells[canonical]);
}

/**
 * Stacking order inside the grid, stated in one place because the layers only
 * ever overlap once both axes have been scrolled at the same time.
 *
 *   30  a frozen HEADER cell — the corner, so it has to cover both of the below
 *   10  an ordinary header cell (set inline on the cells themselves, on the page)
 *    5  a frozen BODY cell — over the columns sliding under it, under the header
 *   --  every other cell is `static` and paints beneath all of them
 *
 * 5 rather than nothing: an ordinary cell is static, but the tooltips and
 * dropdowns INSIDE one are absolutely positioned at `z-index: auto`, and a later
 * sibling at auto paints over an earlier one. A frozen cell is the first child
 * of its row, so without a number of its own a tooltip six columns to the right
 * would draw across it.
 */
const FROZEN_HEADER_Z = 30;
const FROZEN_BODY_Z = 5;

/**
 * The style that pins one cell to the left edge, merged over whatever the cell
 * already had. Returns `existing` untouched for a column that is not frozen.
 *
 * `top` is deliberately not set here: the header cells carry their own
 * `top: 0` and the body cells must not have one, so this only ever states the
 * horizontal half and lets the spread keep the rest.
 */
function withFrozenLeft(
  offsets: number[] | null,
  display: number,
  existing: React.CSSProperties | undefined,
  zIndex: number
): React.CSSProperties | undefined {
  if (!offsets || display >= offsets.length) return existing;
  return { ...existing, position: 'sticky', left: offsets[display], zIndex };
}

/**
 * One row's cells, in display order, with the leading `FROZEN_COLUMN_COUNT`
 * pinned to the left edge so a row stays identifiable however far right the
 * grid is scrolled.
 *
 * `React.Children.toArray` gives every child a stable key derived from its
 * position, so React re-parents the DOM nodes on a reorder instead of
 * remounting them — an open dropdown or a cell being edited survives a drag.
 * `cloneElement` below keeps those keys: it only overrides what is passed to it.
 *
 * `rowTint` / `rowAnchor` (owner decision 2026-09-24, RFQ-unit-mismatch nudge
 * on Send): the grid has no single row DOM node to paint or point at — every
 * cell is its own direct grid child, by design (see the file header) — so a
 * row-level statement has to be merged onto EACH cell here instead of at the
 * one call site in `QuotationDetailPage`.
 *
 *   rowTint     `!bg-*` (Tailwind's important modifier — precedent:
 *               `MasterCataloguePage`'s `[&>td]:!bg-cyan-50`) merged onto every
 *               cell that carries NO flag of its own. A cell's own state is
 *               always `flagCellClass(...)` (`utils/lineItemFlags.ts`), which
 *               always pairs a background with `ring-inset` — so "does this
 *               cell already have `ring-inset`" is a reliable, existing
 *               fingerprint for "leave this cell alone", with nothing here
 *               needing to know what the state IS. `!bg-*` is what lets the
 *               tint win over the cell's own plain `bg-white` /
 *               `bg-[#FAFCFB]` default without parsing and stripping it —
 *               Tailwind utilities are equal-specificity, so two background
 *               classes on one element do not compose; one has to be
 *               `!important` to be certain of winning regardless of the
 *               order the build happens to emit them in.
 *   rowAnchor   A stable identifier (the line number) written as
 *               `data-quotation-line` on the row's first cell only, so
 *               `document.querySelector('[data-quotation-line="…"]')` can
 *               scroll a row into view (the Send-quote unit-mismatch dialog's
 *               "Go fix").
 *
 * Cells that already carry their own `title` keep it — this only fills in the
 * ones that have none, so a more specific existing tooltip is never replaced
 * by the row's.
 *
 * R11 (owner decisions 2026-09-25): the tint's tooltip is stated ONCE for the
 * row, never as `aria-label` on every cell. It used to write `aria-label` on
 * every plain cell (the same "only where none exists" guard as `title`), but
 * unlike `title` an `aria-label` REPLACES an element's accessible name rather
 * than adding to it — so a cell that carries an interactive control with its
 * own name (an input, a button) got a screen reader announcement of the
 * amber-row sentence repeated on every column instead of what that control
 * actually is. `title` above still covers sighted hover on every cell; the
 * screen-reader equivalent is a single visually-hidden description appended
 * to the FIRST cell only (also where `rowAnchor`'s `data-quotation-line`
 * lives), not a name stamped onto every one.
 */
export const QuotationGridRowCells: React.FC<{
  layout: GridLayout;
  children: React.ReactNode;
  rowTint?: { className: string; title: string } | null;
  rowAnchor?: string;
}> = ({ layout, children, rowTint, rowAnchor }) => {
  const ordered = orderedChildren(children, layout.order, 'row');
  if (!ordered) return <>{children}</>;

  const offsets = frozenLeftOffsets(layout);
  const hasRowMeta = Boolean(rowTint) || rowAnchor != null;
  if (!offsets && !hasRowMeta) return <>{ordered}</>;

  return (
    <>
      {ordered.map((cell, display) => {
        const frozen = Boolean(offsets) && display < (offsets as number[]).length;
        if (!frozen && !hasRowMeta) return cell;
        // Only ever plain host elements (`<div>`), which is what makes this
        // safe — the same assumption the header wrapper documents below.
        const el = cell as React.ReactElement<React.HTMLAttributes<HTMLElement> & { className?: string }>;
        if (!React.isValidElement(el)) return cell;

        const style = frozen
          ? withFrozenLeft(offsets as number[], display, el.props.style, FROZEN_BODY_Z)
          : el.props.style;

        if (!hasRowMeta) {
          return React.cloneElement(el, { style });
        }

        const existingClassName = typeof el.props.className === 'string' ? el.props.className : '';
        const hasOwnFlag = existingClassName.includes('ring-inset');
        const className =
          rowTint && !hasOwnFlag ? `${existingClassName} ${rowTint.className}`.trim() : existingClassName;
        const title = rowTint && !hasOwnFlag && !el.props.title ? rowTint.title : el.props.title;

        // The ONE screen-reader description for the row, on the first cell only
        // (never `aria-label` on every cell — see the header comment above).
        const isFirstCell = display === 0;
        const children =
          rowTint && !hasOwnFlag && isFirstCell
            ? (
                <>
                  {el.props.children}
                  <span className="sr-only">{rowTint.title}</span>
                </>
              )
            : el.props.children;

        return React.cloneElement(el, {
          style,
          className,
          title,
          children,
          ...(isFirstCell && rowAnchor ? { 'data-quotation-line': rowAnchor } : {}),
        });
      })}
    </>
  );
};

interface HeaderProps {
  layout: GridLayout;
  onMoveColumn: (fromDisplay: number, toDisplay: number) => void;
  onResizeColumn: (canonical: number, px: number) => void;
  onResetColumnWidth: (canonical: number) => void;
  children: React.ReactNode;
}

/**
 * The header cells, in display order, each made draggable (to reorder) and
 * given a resize grip on its right edge.
 *
 * The affordances are added by cloning each cell rather than by editing 23 JSX
 * blocks: the cells describe what the column IS, and this describes how the
 * user manipulates it.
 */
export const QuotationGridHeaderCells: React.FC<HeaderProps> = ({
  layout,
  onMoveColumn,
  onResizeColumn,
  onResetColumnWidth,
  children,
}) => {
  const [dragFrom, setDragFrom] = useState<number | null>(null);
  const [dragOver, setDragOver] = useState<number | null>(null);
  // Which column is being resized, and the geometry the drag started from.
  const resizing = useRef<{ canonical: number; startX: number; startWidth: number } | null>(null);

  const endResize = useCallback(() => {
    resizing.current = null;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  const beginResize = useCallback(
    (canonical: number, event: React.MouseEvent, cellEl: HTMLElement | null) => {
      event.preventDefault();
      event.stopPropagation();
      const startWidth = cellEl ? cellEl.getBoundingClientRect().width : MIN_COLUMN_WIDTH_PX;
      resizing.current = { canonical, startX: event.clientX, startWidth };

      // Cursor and selection are pinned on the BODY for the duration: without
      // it, dragging fast enough to leave the 6px grip flips the cursor back
      // and starts selecting the header text under the pointer.
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';

      const onMove = (e: MouseEvent) => {
        const state = resizing.current;
        if (!state) return;
        onResizeColumn(state.canonical, state.startWidth + (e.clientX - state.startX));
      };
      const onUp = () => {
        endResize();
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };

      // On WINDOW, not the grip: the pointer leaves a 6px target immediately,
      // and a listener on the grip would drop the drag the moment it did.
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [onResizeColumn, endResize]
  );

  const cells = React.Children.toArray(children);
  if (cells.length !== layout.order.length) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.error(
        `[QuotationGrid] header: ${cells.length} cells but the layout has ${layout.order.length} columns. ` +
        'Add or remove the column in QUOTATION_GRID_DEFAULT_WIDTHS. Rendering in source order.'
      );
    }
    return <>{children}</>;
  }

  // A frozen column's HEADING has to travel with it, or scrolling right leaves
  // "#" and "Item Name / Description" pinned under whichever headings happened
  // to scroll into their place. Same offsets as the body cells, from the one
  // function, because the two must agree to the pixel.
  const frozen = frozenLeftOffsets(layout);

  return (
    <>
      {layout.order.map((canonical, display) => {
        // React 19 types `ReactElement.props` as `unknown`, so the shape this
        // clones into is stated rather than inferred. These cells are always
        // plain host elements (`<div>`), which is what makes cloning safe.
        type HeaderCellProps = React.HTMLAttributes<HTMLElement> & {
          className?: string;
          children?: React.ReactNode;
        };
        const cell = cells[canonical] as React.ReactElement<HeaderCellProps>;
        if (!React.isValidElement(cell)) return cell;

        const isDropTarget = dragOver === display && dragFrom !== null && dragFrom !== display;
        // Which side the dragged column would land on, so the line appears
        // where the column is actually going rather than always on the left.
        const dropOnLeft = dragFrom !== null && dragFrom > display;

        const existingClass = typeof cell.props.className === 'string' ? cell.props.className : '';

        return React.cloneElement(cell, {
          key: `col-${canonical}`,
          // Merged, not replaced: the cell's own `position: sticky; top: 0` is
          // what freezes the heading ROW, and this adds the horizontal half.
          style: withFrozenLeft(frozen, display, cell.props.style, FROZEN_HEADER_Z),
          draggable: true,
          onDragStart: (e: React.DragEvent) => {
            // A resize in progress must never also start a column drag.
            if (resizing.current) {
              e.preventDefault();
              return;
            }
            setDragFrom(display);
            e.dataTransfer.effectAllowed = 'move';
            // Firefox refuses to start a drag with no payload set.
            try { e.dataTransfer.setData('text/plain', String(display)); } catch (_) {}
          },
          onDragOver: (e: React.DragEvent) => {
            if (dragFrom === null) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            if (dragOver !== display) setDragOver(display);
          },
          onDrop: (e: React.DragEvent) => {
            e.preventDefault();
            if (dragFrom !== null) onMoveColumn(dragFrom, display);
            setDragFrom(null);
            setDragOver(null);
          },
          onDragEnd: () => {
            setDragFrom(null);
            setDragOver(null);
          },
          className: [
            // The shipped first and last cells carry the table's top corners.
            // Once columns move, those corners have to travel to whichever
            // column is now on the end, or the rounding appears in the middle
            // of the header and the real corners are square.
            existingClass.replace(/\brounded-t[lr]-md\b/g, '').trim(),
            display === 0 ? 'rounded-tl-md' : '',
            display === layout.order.length - 1 ? 'rounded-tr-md' : '',
            'relative select-none cursor-grab active:cursor-grabbing',
            isDropTarget ? (dropOnLeft ? 'shadow-[inset_2px_0_0_0_#0A6AAC]' : 'shadow-[inset_-2px_0_0_0_#0A6AAC]') : '',
            dragFrom === display ? 'opacity-60' : '',
          ].filter(Boolean).join(' '),
          children: (
            <>
              {cell.props.children}
              {/* Resize grip, on the column's right edge. `stopPropagation` on
                  mousedown is what stops the grip from also starting the
                  header's reorder drag. */}
              <span
                role="separator"
                aria-orientation="vertical"
                aria-label="Resize column"
                title="Drag to resize · double-click to reset this column"
                onMouseDown={(e) => {
                  const cellEl = (e.currentTarget as HTMLElement).parentElement;
                  beginResize(canonical, e, cellEl);
                }}
                onDoubleClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onResetColumnWidth(canonical);
                }}
                onDragStart={(e) => e.preventDefault()}
                className="absolute top-0 right-0 h-full w-[6px] cursor-col-resize hover:bg-primary-500/30 active:bg-primary-500/50 z-20"
              />
            </>
          ),
        });
      })}
    </>
  );
};
